import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChatCircleDots, UsersThree } from '@phosphor-icons/react';
import { communityApi, type ChatSummary } from '@/api/client';
import { CHAT_OPEN_EVENT, type ChatRef } from '@/lib/chatDock.core';

/****************
 * MESSAGES INSIDE THE ORB, ON THE WEB (live review #140, Guillaume 10-26:
 * switching the Orb to Messages with users must be obvious, on all 4
 * versions). The phone has its own (components/community/OrbMessages.tsx).
 * Here the list of conversations; opening one puts it in the docked chat box
 * at the bottom of the page (components/community/ChatDock.tsx), where web
 * conversations already happen.
 ****************/
export function OrbMessagesWeb() {
  const { t } = useTranslation('community');
  const list = useQuery({ queryKey: ['community', 'chat'], queryFn: communityApi.chatList, refetchInterval: 30_000 });
  const chats = list.data?.chats ?? [];
  const open = (c: ChatSummary) => {
    const ref: ChatRef = c.kind === 'group'
      ? { kind: 'group', group: c.group, name: c.name }
      : { kind: 'dm', handle: c.handle, name: c.name };
    window.dispatchEvent(new CustomEvent<ChatRef>(CHAT_OPEN_EVENT, { detail: ref }));
  };
  return (
    <div className="flex-1 px-3 py-3 space-y-1 overflow-y-auto min-h-0" data-testid="orb-messages">
      {list.data && !list.data.member && <p className="text-xs text-muted-foreground px-1">{t('messages.membersOnly')}</p>}
      {list.data?.member && chats.length === 0 && <p className="text-xs text-muted-foreground px-1">{t('chat.empty')}</p>}
      {chats.map((c) => (
        <button key={c.kind === 'group' ? `g${c.group}` : c.handle} type="button" onClick={() => open(c)}
          className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-start text-sm hover:bg-white/[0.05]">
          {c.kind === 'group' ? <UsersThree size={16} className="text-muted-foreground" /> : <ChatCircleDots size={16} className="text-muted-foreground" />}
          <span className={`flex-1 truncate ${c.unread ? 'font-semibold' : ''}`}>{c.name}</span>
          {c.unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">{c.unread}</span>}
        </button>
      ))}
    </div>
  );
}
